import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  CreditCard,
  FileWarning,
  MessageSquareText,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, getApiErrorMessage } from '../lib/api'
import { formatDateTime } from '../lib/date'
import { asArray, getUsageDailyTrendPoints } from '../lib/opsUsageSummary'
import { Button } from '../components/ui/button'
import { Skeleton } from '../components/ui/skeleton'

const PERIOD_OPTIONS = [
  { value: '1', label: '오늘' },
  { value: '7', label: '7일' },
  { value: '30', label: '30일' },
]

const MENU_LABELS = {
  MENU_HOME: '홈',
  MENU_CATEGORY_LANDING: '홍보용페이지',
  MENU_SALES_IMAGES: '이미지 자료실',
  MENU_SALES_PUBLIC_CARD: '모바일명함',
  MENU_SALES_PUBLIC_LANDING: '개인홈페이지',
  MENU_SALES_ORG_HOMEPAGE: '조직홈페이지',
  MENU_SALES_CONSULTATIONS: 'DB수집관리',
  MENU_HEALTH: '진료정보분석',
  MENU_FIRE: '화재보험설계',
  MENU_REQUESTS: '설계관리',
  MENU_NOTICES: '공지',
  MENU_SUPPORT_BULK_SMS: '단체문자발송',
  MENU_SUPPORT_KAKAO_BULK: '카카오톡 단체발송',
  MENU_SUPPORT_SURVEYS: '설문관리',
  MENU_SUPPORT_CUSTOMERS: '고객관리',
  MENU_SUPPORT_INQUIRIES: '고객문의',
  MENU_SUPPORT_CLAIMS: '보험금청구',
  MENU_WORK_INSURANCE_NEWS: '보험뉴스',
  MENU_WORK_INSURER_NEWSLETTERS: '보험사 소식지',
  MENU_WORK_INSURER_SYSTEMS: '보험사전산',
  MENU_WORK_CALENDAR: '일정관리',
  MENU_WORK_POLICY_LOOKUP: '약관조회',
  MENU_ORG_DASHBOARD: '조직 대시보드',
  MENU_REPORTS: '통계·리포트',
  MENU_ORGS: '조직관리',
  MENU_USERS: '조직 사용자',
  MENU_CUSTOMERS: '고객업무 허브',
  MENU_UW_EXCEPTIONS: '예외질환조회',
  MENU_USER_TASKS: '오늘의 업무',
  MENU_OTHER: '기타 메뉴',
}

const INQUIRY_CATEGORY_LABELS = {
  CUSTOMER_SUPPORT: '일반·서비스 문의',
  ACCOUNT_RELATED: '계정 문의',
  UW3NN_EXCEPTION: '예외질환 문의',
  FEATURE_REQUEST: '기능 개선 문의',
}

const SERVICE_KEYS = [
  'requestsCreated',
  'healthReportsCreated',
  'fireBuildingLookups',
  'counselSurveySessionsCreated',
]

function unwrapData(response) {
  const payload = response?.data
  return payload?.data !== undefined ? payload.data : payload ?? null
}

function numberValue(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatNumber(value) {
  return numberValue(value).toLocaleString('ko-KR')
}

function formatPercent(value, digits = 1) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? `${parsed.toFixed(digits)}%` : '집계 없음'
}

function sumKeys(source, keys) {
  return keys.reduce((total, key) => total + numberValue(source?.[key]), 0)
}

function deltaFromPrior(current, prior) {
  const currentValue = numberValue(current)
  const priorValue = numberValue(prior)
  if (priorValue <= 0) return currentValue === 0 ? 0 : null
  return (currentValue - priorValue) / priorValue
}

function ageInMinutes(value) {
  if (!value) return null
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) return null
  return Math.max(0, Math.round((Date.now() - timestamp) / 60000))
}

function billingQueuePath(queue) {
  const target = (queue || []).find((item) => numberValue(item?.count) > 0)?.targetMenu
  if (target === 'refunds') return '/subscriptions/hub/refunds'
  if (target === 'orders') return '/subscriptions/hub/orders'
  if (target === 'billing-logs') return '/subscriptions/hub/billing-logs'
  if (target === 'config') return '/subscriptions/config'
  return '/subscriptions/hub/status'
}

function TrendIndicator({ delta }) {
  if (delta == null || !Number.isFinite(Number(delta))) {
    return <span className="text-xs font-medium text-slate-400">비교 기준 없음</span>
  }
  const numeric = Number(delta)
  const positive = numeric > 0
  const negative = numeric < 0
  const Icon = negative ? TrendingDown : TrendingUp
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${
      positive ? 'text-emerald-700' : negative ? 'text-rose-700' : 'text-slate-500'
    }`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {positive ? '+' : ''}{(numeric * 100).toFixed(Math.abs(numeric) >= 0.1 ? 0 : 1)}%
    </span>
  )
}

function MetricCard({ icon: Icon, label, value, detail, delta, tone = 'blue' }) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    violet: 'bg-violet-50 text-violet-700',
    amber: 'bg-amber-50 text-amber-700',
  }
  return (
    <article className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-md ${tones[tone] || tones.blue}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <TrendIndicator delta={delta} />
      </div>
      <p className="mt-4 text-sm font-medium text-slate-600">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{value}</p>
      <p className="mt-1 min-h-5 text-xs text-slate-500">{detail}</p>
    </article>
  )
}

function ActionRow({ icon: Icon, title, description, count, to, tone = 'amber' }) {
  const hasItems = numberValue(count) > 0
  const toneClass = hasItems
    ? tone === 'rose'
      ? 'bg-rose-50 text-rose-700'
      : 'bg-amber-50 text-amber-700'
    : 'bg-emerald-50 text-emerald-700'
  return (
    <Link
      to={to}
      className="group flex min-h-[4.5rem] items-center gap-3 border-b border-slate-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-slate-50"
    >
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${toneClass}`}>
        {hasItems ? <Icon className="h-4 w-4" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block truncate text-xs text-slate-500">{description}</span>
      </span>
      <span className={`text-lg font-bold tabular-nums ${hasItems ? 'text-slate-950' : 'text-emerald-700'}`}>
        {formatNumber(count)}
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
    </Link>
  )
}

function StatusRow({ title, detail, status, tone = 'good' }) {
  const toneClass = {
    good: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-800',
    danger: 'bg-rose-50 text-rose-700',
    neutral: 'bg-slate-100 text-slate-600',
  }[tone]
  return (
    <div className="flex min-h-[4.5rem] items-center justify-between gap-4 border-b border-slate-100 px-4 py-3 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-0.5 truncate text-xs text-slate-500">{detail}</p>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${toneClass}`}>{status}</span>
    </div>
  )
}

function SectionHeader({ title, description, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-base font-bold text-slate-950">{title}</h2>
        {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
      </div>
      {action}
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <Skeleton key={item} className="h-40 w-full rounded-md" />)}
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        <Skeleton className="h-96 w-full rounded-md" />
        <Skeleton className="h-96 w-full rounded-md" />
      </div>
    </div>
  )
}

export default function OpsUsageDashboardPage() {
  const [data, setData] = useState(null)
  const [operations, setOperations] = useState(null)
  const [period, setPeriod] = useState('7')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const requests = [
      api.get('/ops/usage-dashboard/summary'),
      api.get('/signups/pending-count'),
      api.get('/inquiries/unread-counts'),
      api.get('/users/withdrawal-requests/pending-count'),
      api.get('/billing/admin/operation-queue'),
      api.get('/claims/stats/summary'),
    ]
    const results = await Promise.allSettled(requests)
    if (results[0].status === 'rejected') {
      setData(null)
      setOperations(null)
      setError(getApiErrorMessage(results[0].reason, '운영 현황을 불러오지 못했습니다.'))
      setLoading(false)
      return
    }

    const optional = (index) => results[index].status === 'fulfilled' ? unwrapData(results[index].value) : null
    setData(unwrapData(results[0].value))
    setOperations({
      signupPending: optional(1),
      inquiryCounts: optional(2),
      withdrawalPending: optional(3),
      billingQueue: optional(4),
      claimStats: optional(5),
      unavailableCount: results.slice(1).filter((result) => result.status === 'rejected').length,
    })
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const view = useMemo(() => {
    if (!data) return null
    const rolling = data?.rolling?.[period] || {}
    const trends = data?.rollingTrendVsPriorPeriod?.[period] || {}
    const serviceExecutions = sumKeys(rolling, SERVICE_KEYS)
    const priorServiceExecutions = SERVICE_KEYS.reduce(
      (total, key) => total + numberValue(trends?.[key]?.priorValue),
      0,
    )
    const membersActive = numberValue(data?.membersActive)
    const loginsToday = numberValue(data?.distinctLoginsToday)
    const loginRate = membersActive > 0 ? (loginsToday / membersActive) * 100 : null

    const inquiryCounts = operations?.inquiryCounts || {}
    const inquiryTotal = Object.values(inquiryCounts).reduce((total, count) => total + numberValue(count), 0)
    const inquiryBreakdown = Object.entries(inquiryCounts)
      .filter(([, count]) => numberValue(count) > 0)
      .map(([key, count]) => `${INQUIRY_CATEGORY_LABELS[key] || '기타 문의'} ${formatNumber(count)}건`)
      .join(', ')

    const billingQueue = Array.isArray(operations?.billingQueue) ? operations.billingQueue : []
    const billingTotal = billingQueue.reduce((total, item) => total + numberValue(item?.count), 0)
    const billingSummary = billingQueue
      .filter((item) => numberValue(item?.count) > 0)
      .slice(0, 2)
      .map((item) => `${item.label} ${formatNumber(item.count)}건`)
      .join(', ')

    const claims = operations?.claimStats?.requestOutcomes || {}
    const failedClaims = numberValue(claims.failedRequests)
    const pendingClaims = numberValue(claims.pendingRequests)
    const deliveredClaims = numberValue(claims.deliveredRequests)
    const totalClaims = numberValue(claims.totalRequests)
    const deliveryRate = totalClaims > 0 ? (deliveredClaims / totalClaims) * 100 : null

    const rollupAge = ageInMinutes(data?.rollupHealth?.usageDailyKstMaxUpdatedAt)
    const pageviewAge = ageInMinutes(data?.rollupHealth?.webPageviewsMaxOccurredAt)
    const topMenus = [...asArray(data?.pageviewsByMenu?.[period])]
      .sort((a, b) => numberValue(b?.clicks) - numberValue(a?.clicks))
      .slice(0, 6)
    const topMenuMax = Math.max(1, ...topMenus.map((item) => numberValue(item?.clicks)))

    const publicViews = data?.publicLinkViews?.[period] || {}
    const publicViewsTotal = Object.values(publicViews).reduce((total, count) => total + numberValue(count), 0)
    const dbBuckets = Object.values(data?.dbCollectionStats?.[period] || {})
    const funnel = dbBuckets.reduce((result, bucket) => ({
      views: result.views + numberValue(bucket?.views),
      requests: result.requests + numberValue(bucket?.consultRequests),
      completed: result.completed + numberValue(bucket?.consultCompleted),
    }), { views: 0, requests: 0, completed: 0 })

    const chartRows = getUsageDailyTrendPoints(data?.dailyTrendKst).slice(-14).map((row) => ({
      ...row,
      serviceExecutions: sumKeys(row, SERVICE_KEYS),
    }))

    return {
      rolling,
      trends,
      serviceExecutions,
      serviceDelta: deltaFromPrior(serviceExecutions, priorServiceExecutions),
      loginRate,
      inquiryTotal,
      inquiryBreakdown,
      billingQueue,
      billingTotal,
      billingSummary,
      failedClaims,
      pendingClaims,
      deliveryRate,
      rollupAge,
      pageviewAge,
      topMenus,
      topMenuMax,
      publicViewsTotal,
      funnel,
      chartRows,
    }
  }, [data, operations, period])

  if (loading && !data) return <DashboardSkeleton />

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-5">
        <p className="font-semibold text-rose-800">{error}</p>
        <Button type="button" variant="outline" className="mt-4" onClick={load}>다시 불러오기</Button>
      </div>
    )
  }

  const actionRows = [
    {
      key: 'signup', icon: UserPlus, title: '가입 승인 대기',
      description: '신규 가입 신청을 검토하고 승인합니다.',
      count: operations?.signupPending?.count, to: '/signups', tone: 'amber',
    },
    {
      key: 'inquiry', icon: MessageSquareText, title: '미확인 고객문의',
      description: view?.inquiryBreakdown || '새로 확인할 고객문의가 없습니다.',
      count: view?.inquiryTotal, to: '/support/inquiries', tone: 'amber',
    },
    {
      key: 'withdrawal', icon: UserMinus, title: '탈퇴 승인 대기',
      description: '회원 탈퇴 요청을 확인하고 처리합니다.',
      count: operations?.withdrawalPending?.count, to: '/withdrawal-approvals', tone: 'amber',
    },
    {
      key: 'claims', icon: FileWarning, title: '팩스 발송 확인 필요',
      description: `최근 30일 실패 ${formatNumber(view?.failedClaims)}건 · 처리 중 ${formatNumber(view?.pendingClaims)}건`,
      count: numberValue(view?.failedClaims) + numberValue(view?.pendingClaims), to: '/ops/stats/claims', tone: 'rose',
    },
    {
      key: 'billing', icon: CreditCard, title: '과금 운영 확인 필요',
      description: view?.billingSummary || '과금 운영 큐에 대기 항목이 없습니다.',
      count: view?.billingTotal, to: billingQueuePath(view?.billingQueue), tone: 'rose',
    },
  ].sort((a, b) => numberValue(b.count) - numberValue(a.count))

  const totalActionCount = actionRows.reduce((total, item) => total + numberValue(item.count), 0)
  const periodLabel = PERIOD_OPTIONS.find((item) => item.value === period)?.label || '7일'
  const signupDelta = view?.trends?.signupUsers?.deltaPct
  const pageviewDelta = view?.trends?.pageviewsTotal?.deltaPct

  return (
    <div className="space-y-7 pb-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-950">운영 대시보드</h1>
            {totalActionCount > 0 ? (
              <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                확인 필요 {formatNumber(totalActionCount)}건
              </span>
            ) : (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">확인할 업무 없음</span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">마지막 집계 {formatDateTime(data?.generatedAt)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-1" aria-label="집계 기간">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setPeriod(option.value)}
                aria-pressed={period === option.value}
                className={`min-h-8 rounded px-3 text-sm font-semibold transition-colors ${
                  period === option.value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <Button type="button" variant="outline" size="icon" onClick={load} disabled={loading} title="새로고침">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            <span className="sr-only">새로고침</span>
          </Button>
          <Link
            to="/ops/stats/members"
            className="inline-flex min-h-9 items-center gap-2 rounded-md bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800"
          >
            상세 통계 <BarChart3 className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </header>

      {operations?.unavailableCount > 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          일부 운영 현황을 불러오지 못했습니다. 새로고침 후에도 계속되면 서버 상태를 확인해 주세요.
        </div>
      ) : null}

      <section aria-labelledby="dashboard-core-metrics" className="space-y-3">
        <SectionHeader title="핵심 흐름" description={`${periodLabel} 기준, 직전 동일 기간과 비교합니다.`} />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            icon={Users}
            label="활성 회원"
            value={`${formatNumber(data?.membersActive)}명`}
            detail={`오늘 로그인 ${formatNumber(data?.distinctLoginsToday)}명 · ${formatPercent(view?.loginRate)}`}
            delta={null}
            tone="blue"
          />
          <MetricCard
            icon={UserPlus}
            label="신규 가입"
            value={`${formatNumber(view?.rolling?.signupUsers)}명`}
            detail={`${periodLabel} 동안 생성된 계정`}
            delta={signupDelta}
            tone="emerald"
          />
          <MetricCard
            icon={Activity}
            label="핵심 서비스 실행"
            value={`${formatNumber(view?.serviceExecutions)}회`}
            detail="설계의뢰·진료분석·화재조회·상담설문"
            delta={view?.serviceDelta}
            tone="violet"
          />
          <MetricCard
            icon={BarChart3}
            label="제품 페이지뷰"
            value={`${formatNumber(view?.rolling?.pageviewsTotal)}회`}
            detail="운영자 화면을 제외한 웹·앱 이용량"
            delta={pageviewDelta}
            tone="amber"
          />
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <section aria-labelledby="dashboard-actions" className="space-y-3">
          <SectionHeader
            title="지금 확인할 업무"
            description="대기·실패 항목을 건수가 많은 순서로 표시합니다."
          />
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            {actionRows.map(({ key, ...item }) => <ActionRow key={key} {...item} />)}
          </div>
        </section>

        <section aria-labelledby="dashboard-health" className="space-y-3">
          <SectionHeader title="운영 상태" description="집계 신선도와 주요 처리 결과를 확인합니다." />
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            <StatusRow
              title="업무 집계"
              detail={view?.rollupAge == null ? '마지막 집계 시각을 확인할 수 없습니다.' : `${formatNumber(view.rollupAge)}분 전에 갱신됨`}
              status={view?.rollupAge == null ? '확인 필요' : view.rollupAge <= 120 ? '정상' : '지연'}
              tone={view?.rollupAge != null && view.rollupAge <= 120 ? 'good' : 'warning'}
            />
            <StatusRow
              title="페이지뷰 수집"
              detail={view?.pageviewAge == null ? '최근 수집 시각을 확인할 수 없습니다.' : `${formatNumber(view.pageviewAge)}분 전에 마지막 수집`}
              status={view?.pageviewAge == null ? '확인 필요' : view.pageviewAge <= 60 ? '정상' : '수집 지연'}
              tone={view?.pageviewAge != null && view.pageviewAge <= 60 ? 'good' : 'warning'}
            />
            <StatusRow
              title="팩스 발송 성공률"
              detail="최근 30일 보험금청구 팩스 기준"
              status={view?.deliveryRate == null ? '집계 없음' : formatPercent(view.deliveryRate)}
              tone={view?.deliveryRate == null ? 'neutral' : view.deliveryRate >= 95 ? 'good' : view.deliveryRate >= 85 ? 'warning' : 'danger'}
            />
            <StatusRow
              title="과금 운영 큐"
              detail={view?.billingSummary || '승인·재처리가 필요한 과금 항목이 없습니다.'}
              status={view?.billingTotal > 0 ? `${formatNumber(view.billingTotal)}건 대기` : '정상'}
              tone={view?.billingTotal > 0 ? 'danger' : 'good'}
            />
            <StatusRow
              title="운영 데이터 연결"
              detail="가입·문의·탈퇴·과금·청구 현황 API"
              status={operations?.unavailableCount > 0 ? `${operations.unavailableCount}개 확인 필요` : '정상'}
              tone={operations?.unavailableCount > 0 ? 'warning' : 'good'}
            />
          </div>
        </section>
      </div>

      <section className="space-y-3" aria-labelledby="dashboard-trend">
        <SectionHeader
          title="최근 14일 추세"
          description="가입·로그인·핵심 서비스 실행의 일별 흐름입니다."
          action={<Link to="/ops/stats/service" className="text-sm font-semibold text-blue-700 hover:underline">서비스 통계 보기</Link>}
        />
        <div className="h-80 min-h-[260px] rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={view?.chartRows || []} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="statDate" tick={{ fontSize: 11 }} tickFormatter={(value) => String(value || '').slice(5).replace('-', '/')} minTickGap={20} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: 6, borderColor: '#cbd5e1', fontSize: 12 }}
                formatter={(value, name) => [formatNumber(value), {
                  signupUsers: '신규 가입',
                  distinctLoginUsers: '로그인 사용자',
                  serviceExecutions: '핵심 서비스 실행',
                }[name] || '이용량']}
              />
              <Line type="monotone" dataKey="signupUsers" stroke="#059669" strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="distinctLoginUsers" stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="serviceExecutions" stroke="#7c3aed" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <section className="space-y-3" aria-labelledby="dashboard-acquisition">
          <SectionHeader
            title="유입·상담 전환"
            description={`${periodLabel} 공개 페이지 유입이 상담으로 이어진 흐름입니다.`}
            action={<Link to="/ops/stats/acquisition" className="text-sm font-semibold text-blue-700 hover:underline">유입 통계 보기</Link>}
          />
          <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-4 sm:grid-cols-4">
              {[
                ['공개 링크 조회', view?.publicViewsTotal],
                ['DB 수집 페이지 조회', view?.funnel?.views],
                ['상담 신청', view?.funnel?.requests],
                ['상담 완료', view?.funnel?.completed],
              ].map(([label, value], index) => (
                <div key={label} className="relative min-w-0">
                  <p className="text-xs font-semibold text-slate-500">{label}</p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{formatNumber(value)}</p>
                  {index < 3 ? <ArrowRight className="absolute -right-3 top-5 hidden h-4 w-4 text-slate-300 sm:block" aria-hidden="true" /> : null}
                </div>
              ))}
            </div>
            <div className="mt-5 grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2">
              <div>
                <div className="flex justify-between text-xs font-medium text-slate-600">
                  <span>조회 → 상담 신청</span>
                  <span>{view?.funnel?.views > 0 ? formatPercent((view.funnel.requests / view.funnel.views) * 100) : '집계 없음'}</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, view?.funnel?.views > 0 ? (view.funnel.requests / view.funnel.views) * 100 : 0)}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs font-medium text-slate-600">
                  <span>상담 신청 → 완료</span>
                  <span>{view?.funnel?.requests > 0 ? formatPercent((view.funnel.completed / view.funnel.requests) * 100) : '집계 없음'}</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full bg-emerald-600" style={{ width: `${Math.min(100, view?.funnel?.requests > 0 ? (view.funnel.completed / view.funnel.requests) * 100 : 0)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-3" aria-labelledby="dashboard-menu-rank">
          <SectionHeader title="많이 사용한 메뉴" description={`${periodLabel} 제품 페이지뷰 기준입니다.`} />
          <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            {(view?.topMenus || []).length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-slate-500">집계된 메뉴 이용 데이터가 없습니다.</div>
            ) : (
              <ol className="space-y-3">
                {view.topMenus.map((item, index) => (
                  <li key={`${item.menuKey}-${index}`} className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-2">
                    <span className="text-xs font-bold text-slate-400">{index + 1}</span>
                    <div className="min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate text-sm font-medium text-slate-800">{MENU_LABELS[item.menuKey] || '기타 메뉴'}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full bg-slate-700" style={{ width: `${(numberValue(item.clicks) / view.topMenuMax) * 100}%` }} />
                      </div>
                    </div>
                    <span className="text-sm font-bold tabular-nums text-slate-900">{formatNumber(item.clicks)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}